import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  usePropertyAddressUrl,
  useTokens,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  EqualHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { useEffect, useMemo, type CSSProperties } from 'react';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { relativeLuminance } from '../utils/luminance';

// RentCafe Conversations launcher — sit just above the open header (z-70)
// and offer chrome, without climbing past the real chat wrap.
const CHAT_LAUNCHER_Z = '120';
const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
const CHAT_LAUNCHER_SELECTOR =
  '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';

function pinChatLauncherZ(el: HTMLElement) {
  el.style.setProperty('z-index', CHAT_LAUNCHER_Z, 'important');
}

function outermostFixedAncestor(el: HTMLElement): HTMLElement | null {
  let outermost: HTMLElement | null = null;
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.documentElement) {
    if (getComputedStyle(cur).position === 'fixed') outermost = cur;
    cur = cur.parentElement;
  }
  return outermost;
}

function pinChatLauncherNode(node: Element) {
  if (!(node instanceof HTMLElement)) return;
  if (!node.matches(CHAT_LAUNCHER_SELECTOR)) return;
  pinChatLauncherZ(node);
  // Only raise an outermost fixed ancestor when it is the chat wrap itself —
  // never an unrelated vendor shell that merely contains the toggle.
  const fixed = outermostFixedAncestor(node);
  if (fixed && fixed !== node && fixed.matches(CHAT_WRAP_SELECTOR)) {
    pinChatLauncherZ(fixed);
  }
}

function pinAllChatLaunchers(root: ParentNode = document) {
  root.querySelectorAll(CHAT_LAUNCHER_SELECTOR).forEach(pinChatLauncherNode);
}

// ============================================================================
// Helpers
// ============================================================================

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

function stripCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  // Remove a trailing country segment (USA / U.S.A. / United States [of America]),
  // along with any preceding comma/whitespace.
  return raw
    .replace(/\s*,?\s*(u\.?\s*s\.?\s*a\.?|united\s+states(\s+of\s+america)?)\s*$/i, '')
    .trim();
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined' && normalized !== '0' && normalized !== 'closed';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  label: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the incoming entries by normalized day key so missing days
  // (which the caller may omit entirely to indicate "closed") can be
  // rendered as "Closed" alongside days that were provided.
  const entriesByDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !entriesByDay.has(key)) {
      entriesByDay.set(key, entry);
    }
  }

  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry = entriesByDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const isClosed = !entry || !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
    } else {
      groups.push({ label: dayLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const ctaBannerBgDef: SlotGroupDefinition = {
  label: 'CTA Banner Background',
  description: 'Standout community photo behind the "live your best life" headline. Defaults to the property\'s featured image; swap to a gallery photo if you\'d rather keep this different from the hero.',
  fields: {
    image: {
      label: 'Background Image',
      type: 'image',
      defaultField: 'property_featured_image',
      suggestedFields: [
        'property_featured_image',
        'photo_gallery[0].image',
        'photo_gallery[1].image',
        'photo_gallery[2].image',
        'photo_gallery[3].image',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaPrimaryButtonDef: SlotGroupDefinition = {
  label: 'CTA Primary Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Contact Us' },
    url: { label: 'URL', type: 'url', defaultValue: '/contact' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const ctaSecondaryButtonDef: SlotGroupDefinition = {
  label: 'CTA Secondary Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'FAQs' },
    url: { label: 'URL', type: 'url', defaultValue: '/faq' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentLoginLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Main Street, City, ST 00000' },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: { label: 'Phone Text', type: 'text', defaultField: 'property_call_tracking_phone' },
    url: { label: 'URL', type: 'url', defaultField: 'property_call_tracking_phone' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_logo', suggestedFields: ['property_company_logo', 'property_company_mono_black_logo', 'property_company_mono_white_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const leasingCtaDef: SlotGroupDefinition = {
  label: 'Leasing Call to Action',
  fields: {
    headline: { label: 'Headline', type: 'text', defaultValue: 'Ready to See It in Person?' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility Statement' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  useSiteCtaStyles();
  const propertyName = useContentToken('footer.property_name');
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const propertyAddressUrl = usePropertyAddressUrl();

  // Vendor injects the Conversations launcher after mount and may rewrite
  // inline z-index. Stylesheet !important beats plain inline; the observer
  // re-pins when the launcher (re)appears or its style attribute changes.
  useEffect(() => {
    pinAllChatLaunchers();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.target instanceof Element) {
          pinChatLauncherNode(mutation.target);
          continue;
        }
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            pinChatLauncherNode(node);
            pinAllChatLaunchers(node);
          });
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'id'],
    });

    return () => observer.disconnect();
  }, []);

  // The footer body (community logo) and the attribution band (powered-by /
  // designed-by logos) both sit on --vibe-color-secondary. Pick the mono logo
  // variant with the best contrast against that surface: white/light logo on a
  // dark secondary, black/color logo on a light secondary. Fall back to the
  // hard-coded default dark green (#3e441d) — which is dark, hence the white
  // variant — when the token is unavailable (SSR / missing tokens).
  const tokens = useTokens();
  const useDarkLogo = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return false;
    // > 0.55 luminance ≈ "clearly light background", the same threshold the
    // SpecialOfferBanner uses for its fallback text color pick.
    return relativeLuminance(secondary) > 0.55;
  }, [tokens]);

  // Runtime-computed slot-group def for the community logo: swap the default
  // property field between mono-white and mono-black based on the footer
  // background so themes whose secondary is pale still get a readable logo.
  // The static logoSlotDef stays exported in contentSchema for the editor UI
  // and schema linter — the editor still shows the full suggestedFields list.
  const effectiveLogoDef = useMemo<SlotGroupDefinition>(() => ({
    ...logoSlotDef,
    fields: {
      ...logoSlotDef.fields,
      image: {
        ...logoSlotDef.fields.image,
        defaultField: useDarkLogo ? 'property_mono_black_logo' : 'property_mono_white_logo',
      },
    },
  }), [useDarkLogo]);

  // Same auto-contrast treatment for the "Managed By" management-company logo.
  // Static managedByLogoDef defaults to property_company_logo (full color),
  // which can be unreadable on either a very light or very dark footer —
  // swap to the mono variant that contrasts with the actual secondary color.
  //
  // BUT: many properties only have the full-color `property_company_logo`
  // populated and no mono variants. Forcing `defaultField` to a missing mono
  // field would resolve to empty, and the conditional `{managedByLogo.image
  // ? … : null}` render gate below would hide the whole "Managed By:" group.
  // To avoid that, walk a preference list and pick the first field that
  // actually has data on the property: preferred-contrast mono → other mono
  // → full-color logo. If none are populated the group stays hidden as
  // before.
  const companyMonoBlackLogo = usePropertyField('property_company_mono_black_logo') as string | null | undefined;
  const companyMonoWhiteLogo = usePropertyField('property_company_mono_white_logo') as string | null | undefined;
  const companyColorLogo = usePropertyField('property_company_logo') as string | null | undefined;

  const effectiveManagedByLogoDef = useMemo<SlotGroupDefinition>(() => {
    const preferredField = useDarkLogo
      ? 'property_company_mono_black_logo'
      : 'property_company_mono_white_logo';
    const otherMonoField = useDarkLogo
      ? 'property_company_mono_white_logo'
      : 'property_company_mono_black_logo';
    const preferredValue = useDarkLogo ? companyMonoBlackLogo : companyMonoWhiteLogo;
    const otherMonoValue = useDarkLogo ? companyMonoWhiteLogo : companyMonoBlackLogo;

    let resolvedField: string;
    if (preferredValue) resolvedField = preferredField;
    else if (otherMonoValue) resolvedField = otherMonoField;
    else if (companyColorLogo) resolvedField = 'property_company_logo';
    else resolvedField = preferredField; // no data anywhere — group stays hidden

    return {
      ...managedByLogoDef,
      fields: {
        ...managedByLogoDef.fields,
        image: {
          ...managedByLogoDef.fields.image,
          defaultField: resolvedField,
        },
      },
    };
  }, [useDarkLogo, companyMonoBlackLogo, companyMonoWhiteLogo, companyColorLogo]);

  // Slot groups
  const ctaBannerBg = useSlotGroup('footer.cta_banner_background', ctaBannerBgDef);
  const ctaPrimaryButton = useSlotGroup('footer.cta_primary_button', ctaPrimaryButtonDef);
  const ctaSecondaryButton = useSlotGroup('footer.cta_secondary_button', ctaSecondaryButtonDef);
  const logo = useSlotGroup('footer.logo', effectiveLogoDef);
  const applyNowLink = useSlotGroup('footer.apply_now_link', applyNowLinkDef);
  const residentLoginLink = useSlotGroup('footer.resident_login_link', residentLoginLinkDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const phoneLink = useSlotGroup('footer.phone_link', phoneLinkDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', effectiveManagedByLogoDef);
  const leasingCta = useSlotGroup('footer.leasing_cta', leasingCtaDef);
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);

  // Office hours
  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Derived URLs
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '555-555-5555';

  return (
    <footer className="footer-root">

      {/* ============================================================ */}
      {/* CTA Banner — "Live your best life at ..."                    */}
      {/* ============================================================ */}
      <section className="footer-cta-banner">
        <EditableSlotGroup groupPath="footer.cta_banner_background" definition={ctaBannerBgDef}>
          <div className="footer-cta-banner-media" aria-hidden="true">
            {ctaBannerBg.image ? (
              <VibesitesImage
                url={ctaBannerBg.image}
                alt=""
                sizes="100vw"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-[var(--vibe-color-secondary,#3e441d)]" />
            )}
          </div>
        </EditableSlotGroup>
        <div className="footer-cta-banner-overlay" aria-hidden="true" />

        <div className="footer-cta-banner-inner max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <RichTextContent
            path="footer.cta_banner_headline"
            defaultValue={'<p>Live your best life at <span data-type="field-token" data-field="property_name"></span>.</p>'}
            as="h2"
            className="footer-cta-banner-headline"
          />

          <div
            className="footer-cta-banner-actions"
            style={
              {
                // Banner sits on a dark green background, so remap the CTA
                // accents to stay legible while preserving the two-tier
                // primary/secondary treatment used site-wide.
                '--site-cta-primary-accent': '#ffffff',
                '--site-cta-primary-on-accent': 'var(--vibe-color-primary)',
                '--site-cta-secondary-accent': 'var(--vibe-color-primary)',
                '--site-cta-secondary-on-accent': 'var(--vibe-color-text-on-primary, #ffffff)',
                '--site-cta-hover-accent': '#ffffff',
                '--site-cta-hover-on-accent': 'var(--vibe-color-primary)',
              } as CSSProperties
            }
          >
            <EditableSlotGroup groupPath="footer.cta_primary_button" definition={ctaPrimaryButtonDef}>
              <a
                href={ctaPrimaryButton.url || '/contact'}
                target={ctaPrimaryButton._target || '_self'}
                className="site-cta site-cta--primary"
              >
                {(ctaPrimaryButton.label || 'Contact Us').toUpperCase()}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.cta_secondary_button" definition={ctaSecondaryButtonDef}>
              <a
                href={ctaSecondaryButton.url || '/faq'}
                target={ctaSecondaryButton._target || '_self'}
                className="site-cta site-cta--secondary"
              >
                {(ctaSecondaryButton.label || 'FAQs').toUpperCase()}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

        {/* Dashed divider */}
        <div className="footer-divider" aria-hidden="true" />

        {/* ============================================================ */}
        {/* Main grid: logo + link columns                              */}
        {/* ============================================================ */}
        <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_0.9fr_1.5fr] gap-y-10 gap-x-8">

          {/* Logo */}
          <div className="flex flex-col items-center sm:items-start gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={effectiveLogoDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-20 w-auto max-w-[180px] object-contain object-left"
                  />
                ) : (
                  <span className="footer-heading text-2xl">
                    {propertyName || 'Stadium House'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>
          </div>

          {/* RESIDENTS */}
          <div className="footer-col">
            <RichTextContent
              path="footer.residents_label"
              defaultValue="<p>RESIDENTS</p>"
              as="h3"
              className="footer-col-heading"
            />
            <EditableSlotGroup groupPath="footer.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                className="footer-link footer-link-upper"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="footer.resident_login_link" definition={residentLoginLinkDef}>
              <a
                href={residentLoginLink.url || '/resident-access'}
                target={residentLoginLink._target || '_self'}
                className="footer-link footer-link-upper"
              >
                {residentLoginLink.label || 'Resident Login'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* VISIT US */}
          <div className="footer-col">
            <RichTextContent
              path="footer.visit_label"
              defaultValue="<p>VISIT US</p>"
              as="h3"
              className="footer-col-heading"
            />
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="footer-link"
              >
                {stripCountry(addressLink.text) || '123 Main Street, City, ST 00000'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* CONTACT US */}
          <div className="footer-col">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>CONTACT US</p>"
              as="h3"
              className="footer-col-heading"
            />
            <EditableSlotGroup groupPath="footer.phone_link" definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                className="footer-link dniphone dniphonehref"
              >
                {phoneDisplay}
              </a>
            </EditableSlotGroup>
          </div>

          {/* HOURS */}
          <div className="footer-col">
            <RichTextContent
              path="footer.hours_label"
              defaultValue="<p>HOURS</p>"
              as="h3"
              className="footer-col-heading"
            />
            {groupedHours.length > 0 ? (
              <dl className="footer-hours">
                {groupedHours.map((group, index) => (
                  <div key={index} className="footer-hours-row">
                    <dt>{group.label}</dt>
                    <dd>{group.hours}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="footer-link opacity-60">Office hours will be displayed here.</p>
            )}
          </div>

        </div>

      </div>

      {/* ============================================================ */}
      {/* Attribution band: leasing CTA + logos + social (light)      */}
      {/* ============================================================ */}
      <div className="footer-attr-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">

          {/* Dashed divider */}
          <div className="footer-attr-divider" aria-hidden="true" />

          <div className="footer-bottom-inner mt-10 md:mt-14">

            {/* Leasing CTA */}
            <EditableSlotGroup groupPath="footer.leasing_cta" definition={leasingCtaDef}>
              <div className="footer-leasing-card">
                <p className="footer-leasing-headline">
                  {leasingCta.headline || 'Ready to See It in Person?'}
                </p>
                <a
                  href={leasingCta.url || '/schedule-a-tour'}
                  target={leasingCta._target || '_self'}
                  className="site-cta site-cta--secondary"
                >
                  {leasingCta.label || 'Schedule a Tour'}
                </a>
              </div>
            </EditableSlotGroup>

            {/* Attribution */}
            <div className="footer-attribution">

              {/* Managed by (optional) */}
              {managedByLogo.image ? (
                <div className="footer-attr-group">
                  <span className="footer-attr-label">Managed By:</span>
                  <EditableSlotGroup groupPath="footer.managed_by_logo" definition={effectiveManagedByLogoDef}>
                    <a href={managedByLogo.url || '#'} target={managedByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                      <VibesitesImage
                        url={managedByLogo.image}
                        alt={managedByLogo.alt || 'Property Management Company'}
                        className="h-11 w-auto max-w-[200px] object-contain"
                      />
                    </a>
                  </EditableSlotGroup>
                </div>
              ) : null}

              {/* Powered by */}
              <div className="footer-attr-group">
                <span className="footer-attr-label">Powered By:</span>
                <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
                  <a href={poweredByLogo.url || 'https://www.multihub.io/'} target={poweredByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                    {useDarkLogo ? (
                      <MultihubLogoMonoBlack height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    ) : (
                      <MultihubLogoMonoWhite height={22} alt={poweredByLogo.alt || 'Multihub'} />
                    )}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Follow us on social */}
              <div className="footer-attr-group">
                <span className="footer-attr-label">Follow Us On Social</span>
                <div className="flex items-center gap-3">
                  <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                    {facebookLink.url ? (
                      <a
                        href={facebookLink.url}
                        target={facebookLink._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={facebookLink.label || 'Facebook'}
                        className="footer-social"
                      >
                        <FontAwesomeIcon icon={faFacebookF} className="text-lg" />
                      </a>
                    ) : (
                      <span className="footer-social opacity-40">
                        <FontAwesomeIcon icon={faFacebookF} className="text-lg" />
                      </span>
                    )}
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                    {instagramLink.url ? (
                      <a
                        href={instagramLink.url}
                        target={instagramLink._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={instagramLink.label || 'Instagram'}
                        className="footer-social"
                      >
                        <FontAwesomeIcon icon={faInstagram} className="text-lg" />
                      </a>
                    ) : (
                      <span className="footer-social opacity-40">
                        <FontAwesomeIcon icon={faInstagram} className="text-lg" />
                      </span>
                    )}
                  </EditableSlotGroup>
                </div>
              </div>

              {/* Created by */}
              <div className="footer-attr-group">
                <span className="footer-attr-label">Created By:</span>
                <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
                  <a href={designByLogo.url || 'https://www.repli360.com/'} target={designByLogo._target || '_blank'} rel="noopener noreferrer" className="opacity-90 hover:opacity-100 transition-opacity">
                    {/* @vibesites/assets ships only a white Repli SVG. Its
                        wordmark is a mono glyph we invert for light footers
                        (below), BUT the file also embeds a raster gradient
                        dot (blue → magenta → pink) which reads as coloured
                        on dark backgrounds and flips to yellow/green under
                        `invert()`. Force the dot to a solid black by
                        overlaying a positioned circle as a sibling of the
                        image element (not a child of the filtered node),
                        so no CSS filter touches it. Coordinates come from
                        the SVG's clipPath: viewBox 1015.22×495.97, dot
                        centered near (976, 88.5) with radius ≈36 →
                        96.14% left, 17.84% top, ~7.09% width (kept round
                        via aspect-ratio 1/1). */}
                    <span className="footer-repli-mark">
                      <img
                        src={repliWhiteLogoUrl}
                        alt={designByLogo.alt || 'Repli'}
                        className="h-11 w-auto object-contain block"
                        style={useDarkLogo ? { filter: 'invert(1)' } : undefined}
                      />
                      <span
                        className="footer-repli-dot"
                        aria-hidden="true"
                        // Match the dot to the wordmark: white on dark footers
                        // (forced-white logo) and black on light footers where
                        // the SVG is inverted to a black wordmark. Without
                        // this, the dot stays black on dark bg and reads as
                        // a random dark speck next to a white logo.
                        style={{ background: useDarkLogo ? '#000' : '#fff' }}
                      />
                    </span>
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Compliance strip (primary background)                        */}
      {/* ============================================================ */}
      <div className="footer-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col items-center gap-3 text-center">
          <div className="footer-compliance" aria-label="Fair housing and accessibility">
            <EqualHousingIcon size={26} className="footer-compliance-icon" aria-label="Equal Housing Opportunity" />
            <PetsAllowedIcon size={22} className="footer-compliance-icon" aria-label="Pet Friendly" />
            <WheelchairIcon size={22} className="footer-compliance-icon" aria-label="Accessibility" />
          </div>

          <p className="footer-strip-copyright">
            &copy; Copyright {new Date().getFullYear()}{' '}
            <EditableToken
              path="footer.property_name"
              defaultField="property_name"
              defaultValue="Stadium House"
            >
              {propertyName || 'Stadium House'}
            </EditableToken>
          </p>

          <nav className="footer-strip-legal" aria-label="Footer legal links">
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a
                href={privacyPolicyLink.url || '/privacy-policy'}
                target={privacyPolicyLink._target || '_self'}
                className="footer-strip-link"
              >
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span className="footer-strip-sep" aria-hidden="true">·</span>
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a
                href={accessibilityLink.url || '/accessibility'}
                target={accessibilityLink._target || '_self'}
                className="footer-strip-link"
              >
                {accessibilityLink.text || 'Accessibility Statement'}
              </a>
            </EditableSlotGroup>
            <span className="footer-strip-sep" aria-hidden="true">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a
                href={sitemapLink.url || '/sitemap'}
                target={sitemapLink._target || '_self'}
                className="footer-strip-link"
              >
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Scoped Styles                                                   */}
      {/* ================================================================ */}
      <style>{`
        /* RentCafe Conversations launcher — keep !important so vendor inline
           z-index loses; 120 sits just above the open header. Scoped to the
           real launcher only (no broad yardi/rentcafe iframe or text matches). */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }

        .footer-root {
          --footer-ink: var(--vibe-color-text-on-secondary, #ffffff);
          background-color: var(--vibe-color-secondary, #3e441d);
          color: var(--footer-ink);
        }

        /* ================================================================ */
        /* CTA banner (top of footer)                                       */
        /* ================================================================ */
        .footer-cta-banner {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          background-color: var(--vibe-color-secondary, #3e441d);
          color: #ffffff;
        }
        .footer-cta-banner-media {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .footer-cta-banner-media > * {
          width: 100%;
          height: 100%;
        }
        .footer-cta-banner-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: linear-gradient(
            to right,
            rgba(0, 0, 0, 0.72) 0%,
            rgba(0, 0, 0, 0.55) 45%,
            rgba(0, 0, 0, 0.45) 100%
          );
        }
        .footer-cta-banner-inner {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1.5rem;
          padding-top: 2.75rem;
          padding-bottom: 2.75rem;
          min-height: 12rem;
        }
        @media (min-width: 768px) {
          .footer-cta-banner-inner {
            flex-direction: row;
            align-items: center;
            gap: 2.5rem;
            padding-top: 3.5rem;
            padding-bottom: 3.5rem;
            min-height: 15rem;
          }
        }
        .footer-cta-banner-headline {
          font-family: var(--vibe-font-heading, sans-serif);
          font-weight: 700;
          line-height: 1.05;
          font-size: clamp(1.9rem, 4.5vw, 3.5rem);
          color: #ffffff;
          text-shadow: 0 2px 18px rgba(0, 0, 0, 0.35);
          max-width: 22ch;
          margin: 0;
        }
        .footer-cta-banner-headline p,
        .footer-cta-banner-headline span {
          font: inherit;
          color: inherit;
          margin: 0;
        }
        .footer-cta-banner-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .footer-cta-banner-actions {
            gap: 1rem;
            flex-shrink: 0;
          }
        }
        /* CTA banner focus ring uses the banner's inverted white accents so
           it stays visible against the dark overlay. */
        .footer-cta-banner-actions .site-cta:focus-visible {
          outline-color: #ffffff;
        }


        .footer-divider {
          border-top: 2px dashed var(--footer-ink);
          opacity: 0.85;
        }
        .footer-heading,
        .footer-brand-name {
          font-family: var(--vibe-font-heading, sans-serif);
          font-weight: 700;
          color: var(--footer-ink);
          line-height: 1.1;
        }
        .footer-brand-name {
          font-size: 1.5rem;
        }
        .footer-col {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .footer-col-heading {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--footer-ink);
          margin-bottom: 0.25rem;
        }
        .footer-link {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--footer-ink);
          text-decoration: none;
          transition: opacity 0.2s ease;
        }
        .footer-link:hover {
          opacity: 0.7;
        }
        .footer-link-upper {
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }
        .footer-hours {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }
        .footer-hours-row {
          display: grid;
          grid-template-columns: max-content 1fr;
          gap: 0.5rem;
          align-items: baseline;
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.95rem;
          color: var(--footer-ink);
        }
        .footer-hours-row dt {
          font-weight: 600;
          white-space: nowrap;
        }
        .footer-hours-row dd {
          white-space: nowrap;
        }
        .footer-attr-band {
          --attr-ink: var(--vibe-color-text-on-secondary, #ffffff);
          background-color: var(--vibe-color-secondary, #3e441d);
          color: var(--attr-ink);
        }
        .footer-attr-divider {
          border-top: 2px dashed var(--attr-ink);
          opacity: 0.7;
        }
        .footer-bottom-inner {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 2rem 3rem;
        }
        .footer-leasing-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.9rem;
          padding: 1.75rem 2.75rem;
          border: 1px solid var(--attr-ink);
          border-radius: var(--vibe-radius-md, 0.25rem);
          text-align: center;
        }
        .footer-leasing-headline {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 1.35rem;
          line-height: 1.2;
          color: var(--attr-ink);
        }
        /* The leasing CTA reads as an underlined text link rather than a
           filled button — strip the site-cta chrome (background, border,
           min-width, padding) and drop the hover colour swap so it stays
           the same colour as the surrounding text on hover. */
        .footer-leasing-card .site-cta,
        .footer-leasing-card .site-cta:hover {
          background: transparent;
          border-color: transparent;
          color: var(--attr-ink);
          min-width: 0;
          padding: 0;
          transform: none;
          box-shadow: none;
        }
        .footer-leasing-card .site-cta {
          text-decoration: underline;
          text-underline-offset: 4px;
          text-decoration-thickness: 1px;
        }
        .footer-leasing-card .site-cta:hover {
          opacity: 0.7;
        }
        .footer-leasing-link {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--attr-ink);
          text-decoration: underline;
          text-underline-offset: 4px;
          transition: opacity 0.2s ease;
        }
        .footer-leasing-link:hover {
          opacity: 0.7;
        }
        .footer-attribution {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          gap: 1.5rem 2.5rem;
        }
        .footer-attr-group {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }
        .footer-attr-label {
          font-family: var(--vibe-font-heading, sans-serif);
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--attr-ink);
          max-width: 7rem;
        }
        /* On mobile the attribution band stacks; center everything so the
           logos and social icons don't cling to the left edge under the
           narrow leasing card. */
        @media (max-width: 767px) {
          .footer-bottom-inner {
            justify-content: center;
          }
          .footer-attribution {
            justify-content: center;
            width: 100%;
          }
          .footer-attr-group {
            align-items: center;
            text-align: center;
          }
          .footer-attr-label {
            max-width: none;
            text-align: center;
          }
        }
        .footer-social {
          color: var(--attr-ink);
          transition: opacity 0.2s ease;
        }
        .footer-social:hover {
          opacity: 0.7;
        }
        /* Repli logo trademark-dot overlay. The wrapper is inline-block and
           position:relative so the dot can be percentage-positioned over the
           image's rendered box. line-height:0 avoids inline-baseline gaps
           that would offset the overlay from the image. The dot is a
           sibling of the image element (NOT a child), so it is untouched by
           any filter: invert() applied to the image on light-bg themes. */
        .footer-repli-mark {
          position: relative;
          display: inline-block;
          line-height: 0;
        }
        .footer-repli-dot {
          position: absolute;
          /* Values derived from repli_white.svg's clipPath:
             viewBox 1015.22 × 495.97, dot centered at ~(976, 88.5),
             radius ~36 → 96.14% left, 17.84% top, ~7.09% width.
             The background colour is set inline per-render so it can
             follow useDarkLogo (white on dark footers, black on light). */
          top: 17.84%;
          left: 96.14%;
          width: 7.09%;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .footer-strip {
          --strip-ink: var(--vibe-color-text-on-primary, #ffffff);
          background-color: var(--vibe-color-primary, #b28c59);
          color: var(--strip-ink);
        }
        .footer-compliance {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          color: var(--strip-ink);
        }
        .footer-compliance-icon {
          color: var(--strip-ink);
        }
        .footer-strip-copyright {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.85rem;
          color: color-mix(in srgb, var(--strip-ink) 90%, transparent);
        }
        .footer-strip-legal {
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: center;
          gap: 0.35rem 0.5rem;
        }
        @media (min-width: 480px) {
          .footer-strip-legal {
            gap: 0.5rem 0.75rem;
            flex-wrap: wrap;
          }
        }
        .footer-strip-link {
          font-family: var(--vibe-font-body, sans-serif);
          font-size: 0.75rem;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: var(--strip-ink);
          text-decoration: none;
          transition: opacity 0.2s ease;
          white-space: nowrap;
        }
        @media (min-width: 480px) {
          .footer-strip-link {
            font-size: 0.8rem;
          }
        }
        .footer-strip-link:hover {
          opacity: 0.7;
          text-decoration: underline;
        }
        .footer-strip-sep {
          color: color-mix(in srgb, var(--strip-ink) 55%, transparent);
        }

        /* Mobile-only: center the whole footer stack (CTA banner, columns,
           attribution). On sm+ the columns and attribution rows regain the
           left-aligned block layout via the existing rules above. */
        @media (max-width: 767.98px) {
          .footer-cta-banner-inner {
            align-items: center;
            text-align: center;
          }
          .footer-cta-banner-headline {
            margin-left: auto;
            margin-right: auto;
          }
          .footer-cta-banner-actions {
            justify-content: center;
            width: 100%;
          }
          .footer-col {
            align-items: center;
            text-align: center;
          }
          .footer-hours-row {
            grid-template-columns: auto auto;
            gap: 0.75rem;
            justify-content: center;
          }
          .footer-bottom-inner {
            justify-content: center;
          }
          .footer-attribution {
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 2rem;
          }
          .footer-attr-group {
            align-items: center;
            text-align: center;
          }
          .footer-attr-label {
            max-width: none;
          }
          /* Keep "Ready to See It in Person?" on a single line on mobile.
             Tighten the card's horizontal padding and headline size so the
             nowrap text still fits within the narrow viewport. */
          .footer-leasing-card {
            padding: 1.5rem 1.25rem;
            width: 100%;
            max-width: 100%;
          }
          .footer-leasing-headline {
            white-space: nowrap;
            font-size: 1.15rem;
          }
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Stadium House',
    },
    'footer.cta_banner_headline': {
      label: 'CTA Banner Headline',
      type: 'richtext',
      defaultValue:
        '<p>Live your best life at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'footer.residents_label': {
      label: 'Residents Column Heading',
      type: 'richtext',
      defaultValue: '<p>RESIDENTS</p>',
    },
    'footer.visit_label': {
      label: 'Visit Us Column Heading',
      type: 'richtext',
      defaultValue: '<p>VISIT US</p>',
    },
    'footer.contact_label': {
      label: 'Contact Us Column Heading',
      type: 'richtext',
      defaultValue: '<p>CONTACT US</p>',
    },
    'footer.hours_label': {
      label: 'Hours Column Heading',
      type: 'richtext',
      defaultValue: '<p>HOURS</p>',
    },
  },
  slotGroups: {
    'footer.cta_banner_background': ctaBannerBgDef,
    'footer.cta_primary_button': ctaPrimaryButtonDef,
    'footer.cta_secondary_button': ctaSecondaryButtonDef,
    'footer.logo': logoSlotDef,
    'footer.apply_now_link': applyNowLinkDef,
    'footer.resident_login_link': residentLoginLinkDef,
    'footer.address': addressLinkDef,
    'footer.phone_link': phoneLinkDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.leasing_cta': leasingCtaDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
